<script lang="ts">
    import { onDestroy, onMount } from "svelte"
    import { fade, scale } from "svelte/transition"
    import { cn } from "@/lib/utils"

    /** Real progress (0-100) when the caller knows it. Otherwise the bar creeps forward on its own. */
    export let percent: number | null = null
    /** Fills the bar, then shows the success state (and the `done` slot, e.g. a button). */
    export let done = false
    /**
     * Events on the user's Pusher channel carrying {percent, msg} that drive the bar, e.g. ['progress']. Events that also
     * carry {processed, total} (activities being processed) give an estimate of the time left.
     */
    export let listen: string[] = []
    /** Cycling flavoured messages shown while no message comes from the server. */
    export let messages: string[] = [
        "Clipping in…",
        "Finding your rhythm…",
        "Spinning up the numbers…",
        "Climbing through your data…",
        "Hammering out the details…",
        "Sprinting to the line…",
    ]
    export let title = ""
    /** A line under the title, e.g. how long the wait can be. */
    export let subtitle = ""
    /** Time remaining (ms) when the caller knows it, instead of the estimate from {processed, total} events. Null hides it. */
    export let timeLeftMs: number | null | undefined = undefined
    export let doneTitle = "All done!"
    export let doneMessage = ""
    /** Cover the page instead of sitting inline. */
    export let overlay = false
    let className = ""
    export {className as class}

    // The bar never goes backwards, and never claims to be finished before `done`.
    const CREEP_LIMIT = 92   // Without real progress, the bar slows down as it approaches this.
    const CREEP_AHEAD = 4    // Once progress is being reported, the bar only creeps this far past it.
    const HOLD_LIMIT = 97
    let shown = 0
    let creep = 0
    let real = 0
    let serverMessage = ""
    let messageIndex = 0

    $: if (percent !== null) real = Math.max(real, Math.min(100, percent))

    // @ts-ignore
    const Xert = window.Xert
    // Time remaining, once ESTIMATE_AFTER items are processed: the time per item since the first one reported, for the items left.
    // It counts down between events, and is gone once all are processed (other work may follow them).
    const ESTIMATE_AFTER = 5
    let first: {at: number, processed: number} | null = null
    let processedSoFar = 0
    let estimate: {at: number, ms: number} | null = null
    let now = Date.now()
    const onProgress = (data: {percent?: number, msg?: string, processed?: number, total?: number}) => {
        if (typeof data?.percent === "number") real = Math.max(real, Math.min(100, data.percent))
        if (data?.msg) serverMessage = data.msg
        if (typeof data?.processed === "number" && typeof data?.total === "number" && data.total > 0 && data.processed > processedSoFar) {
            // Events can arrive out of order: only a higher count moves the estimate
            const at = Date.now()
            processedSoFar = data.processed
            if (!first) first = {at, processed: data.processed}
            if (data.processed >= data.total) estimate = null
            else if (data.processed >= ESTIMATE_AFTER && data.processed > first.processed) {
                const msPerItem = (at - first.at)/(data.processed - first.processed)
                estimate = {at, ms: msPerItem*(data.total - data.processed)}
            }
        }
    }
    $: timeLeft = timeLeftMs !== undefined ? (done ? null : timeLeftMs) : estimate && !done ? Math.max(0, estimate.ms - (now - estimate.at)) : null
    function formatTimeLeft(ms: number) {
        const minutes = Math.ceil(ms/60_000)
        return ms < 60_000 ? "less than a minute remaining" : `about ${minutes} ${minutes === 1 ? "minute" : "minutes"} remaining`
    }

    let tick: ReturnType<typeof setInterval>
    let messageTick: ReturnType<typeof setInterval>
    onMount(() => {
        for (const event of listen) Xert?._userChannel?.bind(event, onProgress)
        tick = setInterval(() => {
            // Don't run far ahead of reported progress, or the bar sits near the end while the work is half done.
            const creepLimit = real > 0 ? Math.min(CREEP_LIMIT, real + CREEP_AHEAD) : CREEP_LIMIT
            creep = Math.max(creep, creep + (creepLimit - creep) * 0.012)
            const target = done ? 100 : Math.min(HOLD_LIMIT, Math.max(real, creep))
            shown = Math.max(shown, shown + (target - shown) * (done ? 0.2 : 0.12))
            if (done && shown > 99.5) shown = 100
            if (estimate) now = Date.now()
        }, 100)
        messageTick = setInterval(() => messageIndex = (messageIndex + 1) % messages.length, 2800)
    })
    onDestroy(() => {
        for (const event of listen) Xert?._userChannel?.unbind(event, onProgress)
        clearInterval(tick)
        clearInterval(messageTick)
    })

    $: message = serverMessage || messages[messageIndex]
    $: finished = done && shown >= 100

    /*
     * The rider: a road bike and rider in a 100 x 60 box, the wheels on the bottom edge so they sit on the road. The cranks
     * turn about the bottom bracket, and each leg follows its pedal: hip on the saddle, knee where the thigh and shin
     * meet (ahead of the line from hip to pedal), a frame for every twelfth of a turn, animated with the cranks.
     */
    const BB = {x: 46, y: 45}            // bottom bracket
    const HIP = {x: 41, y: 21}
    const CRANK = 5.5, THIGH = 15.5, SHIN = 15.5
    const PEDAL_SECONDS = 0.7            // one turn of the cranks (about 85 rpm)
    const FRAMES = 12
    function knee(p: {x: number, y: number}) {
        const dx = p.x - HIP.x, dy = p.y - HIP.y, d = Math.hypot(dx, dy)
        const a = (THIGH*THIGH - SHIN*SHIN + d*d)/(2*d), h = Math.sqrt(Math.max(0, THIGH*THIGH - a*a))
        const mx = HIP.x + a*dx/d, my = HIP.y + a*dy/d
        const k1 = {x: mx + h*dy/d, y: my - h*dx/d}, k2 = {x: mx - h*dy/d, y: my + h*dx/d}
        return k1.x > k2.x ? k1 : k2
    }
    // The leg's path at each twelfth of a turn from the pedal straight ahead, clockwise (forward), and back to it
    function legFrames(phase: number) {
        return Array.from({length: FRAMES + 1}, (_, i) => {
            const angle = phase + 2*Math.PI*i/FRAMES
            const pedal = {x: BB.x + CRANK*Math.cos(angle), y: BB.y + CRANK*Math.sin(angle)}
            const k = knee(pedal)
            const f = (v: number) => v.toFixed(2)
            return `M${f(HIP.x)} ${f(HIP.y)}L${f(k.x)} ${f(k.y)}L${f(pedal.x)} ${f(pedal.y)}l3.2 0.4`
        })
    }
    const nearLeg = legFrames(0)
    const farLeg = legFrames(Math.PI)
    const reduceMotion = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches

    const confetti = Array.from({length: 28}, (_, i) => ({
        angle: (i / 28) * 360 + Math.random() * 12,
        distance: 90 + Math.random() * 110,
        delay: Math.random() * 0.15,
        size: 6 + Math.random() * 6,
        color: ["#00a5b5", "#a4d233", "#f5b700", "#ff6b6b", "#5b8def"][i % 5],
        round: i % 3 === 0,
    }))
</script>

<div class={cn("cycling-progress flex items-center justify-center", overlay ? "fixed inset-0 z-[2000] bg-white/85 backdrop-blur-sm" : "w-full py-10", className)} role="status" aria-live="polite">
    <div class="flex flex-col items-center gap-6 w-full max-w-xl px-6 text-center">
        {#if !finished}
            <div class="flex flex-col items-center gap-6 w-full" out:fade={{duration: 200}}>
                {#if title}<div class="text-2xl font-semibold text-slate-700">{title}</div>{/if}
                {#if subtitle}<div class="-mt-4 text-base text-slate-500">{subtitle}</div>{/if}

                <div class="relative w-full pt-14">
                    <!-- Rider, riding along the leading edge of the bar -->
                    <svg class="rider" style="left: {shown}%" viewBox="0 0 100 60" aria-hidden="true">
                        <!-- Speed lines trailing the rear wheel -->
                        <g class="speed-lines">
                            <path d="M2 31H9" style="animation-delay: 0s"/><path d="M0 38H8" style="animation-delay: 0.15s"/><path d="M3 45H8" style="animation-delay: 0.3s"/>
                        </g>
                        <!-- Wheels: tyre, rim, turning spokes, hub -->
                        {#each [22, 78] as cx}
                            <g>
                                <circle class="tyre" {cx} cy="45" r="12.5"/>
                                <circle class="rim" {cx} cy="45" r="10.6"/>
                                <g class="spokes" style="transform-origin: {cx}px 45px">
                                    <path d="M{cx} 34.6V55.4M{cx - 10.4} 45H{cx + 10.4}M{cx - 7.4} 37.6L{cx + 7.4} 52.4M{cx - 7.4} 52.4L{cx + 7.4} 37.6"/>
                                </g>
                                <circle class="hub" {cx} cy="45" r="1.6"/>
                            </g>
                        {/each}
                        <!-- Far leg and crank, behind the frame -->
                        <path class="leg far" d={farLeg[0]}>
                            {#if !reduceMotion}<animate attributeName="d" dur="{PEDAL_SECONDS}s" repeatCount="indefinite" values={farLeg.join(";")}/>{/if}
                        </path>
                        <!-- Frame: chain and seat stays, seat, top and down tubes, fork, seatpost and saddle, stem and drop bars -->
                        <path class="frame" d="M22 45L46 45L40 25L22 45M40 25L69 24L71.5 30L46 45M71.5 30L74 37.5Q76.5 43 78 45M69 24L70.5 19.5"/>
                        <path class="saddle" d="M40 25L39.2 22M35 21.6H43.5"/>
                        <path class="bars" d="M70.5 19.5H75Q79 19.5 78.6 23.5Q78.2 27.5 74.5 27"/>
                        <circle class="chainring" cx={BB.x} cy={BB.y} r="4.2"/>
                        <!-- Rider: bobbing a little with each pedal stroke -->
                        <g class="upper-body">
                            <path class="torso" d="M41 21Q50 11 61.5 12.5"/>
                            <path class="arm" d="M61.5 12.5L66.5 17.5L74.5 19.8"/>
                            <circle class="head" cx="66.5" cy="8.2" r="4"/>
                            <path class="helmet" d="M61.6 8.4Q62 2.6 67.6 3.4Q71.6 4 71.4 8.4Z"/>
                        </g>
                        <!-- Near crank and leg, in front -->
                        <g class="crank">
                            <path d="M{BB.x - CRANK} {BB.y}H{BB.x + CRANK}"/>
                            {#if !reduceMotion}<animateTransform attributeName="transform" type="rotate" from="0 {BB.x} {BB.y}" to="360 {BB.x} {BB.y}" dur="{PEDAL_SECONDS}s" repeatCount="indefinite"/>{/if}
                        </g>
                        <path class="leg" d={nearLeg[0]}>
                            {#if !reduceMotion}<animate attributeName="d" dur="{PEDAL_SECONDS}s" repeatCount="indefinite" values={nearLeg.join(";")}/>{/if}
                        </path>
                    </svg>
                    <div class="road">
                        <div class="road-fill" style="width: {shown}%"></div>
                        {#each [25, 50, 75] as mark}<span class="marker" style="left: {mark}%"></span>{/each}
                    </div>
                </div>

                <div class="flex flex-col items-center gap-1 min-h-[3.5rem]">
                    <span class="text-3xl font-bold tabular-nums text-xerteal-600">{Math.floor(shown)}<span class="text-lg font-medium text-slate-400">%</span></span>
                    {#key message}<span class="text-slate-500 text-base" in:fade={{duration: 300}}>{message}</span>{/key}
                    {#if timeLeft !== null}<span class="text-sm text-slate-500 tabular-nums">{formatTimeLeft(timeLeft)}</span>{/if}
                </div>
            </div>
        {:else}
            <div class="flex flex-col items-center gap-5" in:scale={{start: 0.8, duration: 350}}>
                <div class="relative flex items-center justify-center w-28 h-28">
                    {#each confetti as c}
                        <span class="confetti" style="--angle: {c.angle}deg; --distance: {c.distance}px; --size: {c.size}px; --delay: {c.delay}s; background: {c.color}; border-radius: {c.round ? '50%' : '2px'}"></span>
                    {/each}
                    <svg class="check" viewBox="0 0 52 52" aria-hidden="true">
                        <circle class="check-circle" cx="26" cy="26" r="24" />
                        <path class="check-mark" d="M14 27l8 8 16-17" />
                    </svg>
                </div>
                <div class="text-2xl font-semibold text-slate-700">{doneTitle}</div>
                {#if doneMessage}<div class="text-slate-500 text-base">{doneMessage}</div>{/if}
                <slot name="done" />
            </div>
        {/if}
    </div>
</div>

<style lang="scss">
    .road {
        position: relative;
        height: 14px;
        border-radius: 999px;
        background: #e2e8f0;
        overflow: hidden;
        box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.12);
    }
    .road-fill {
        height: 100%;
        border-radius: 999px;
        background-color: #00a5b5;
        // Moving dashes: the road's centre line rolling past
        background-image: repeating-linear-gradient(90deg, rgba(255, 255, 255, 0.55) 0 14px, transparent 14px 28px);
        background-size: 28px 3px;
        background-repeat: repeat-x;
        background-position: 0 center;
        animation: road-roll 0.6s linear infinite;
    }
    .marker {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 2px;
        background: rgba(15, 23, 42, 0.18);
    }
    .rider {
        position: absolute;
        top: 0;
        width: 96px;
        height: 58px;
        // The bike's middle on the bar's leading edge; its wheels on the road
        transform: translateX(-50%);
        fill: none;
        stroke-linecap: round;
        stroke-linejoin: round;
        overflow: visible;
    }
    .rider .tyre { stroke: #1e293b; stroke-width: 2.6; }
    .rider .rim { stroke: #94a3b8; stroke-width: 1; }
    .rider .spokes { stroke: #94a3b8; stroke-width: 0.6; animation: wheel-spin 0.45s linear infinite; }
    .rider .hub { fill: #475569; }
    .rider .frame { stroke: #00a5b5; stroke-width: 2.6; }
    .rider .saddle, .rider .bars { stroke: #1e293b; stroke-width: 2.2; }
    .rider .chainring { stroke: #475569; stroke-width: 1.4; }
    .rider .crank { stroke: #334155; stroke-width: 1.8; }
    .rider .leg { stroke: #1e293b; stroke-width: 3.6; }
    .rider .leg.far { stroke: #94a3b8; }
    .rider .torso { stroke: #1e293b; stroke-width: 5; }
    .rider .arm { stroke: #1e293b; stroke-width: 2.6; }
    .rider .head { fill: #f1c7a3; }
    .rider .helmet { fill: #a4d233; stroke: #6f9a10; stroke-width: 0.6; }
    // Two small bobs a turn of the cranks
    .rider .upper-body { animation: rider-bob 0.35s ease-in-out infinite alternate; }
    .rider .speed-lines path { stroke: #94a3b8; stroke-width: 1.4; opacity: 0; animation: speed-line 0.6s linear infinite; }

    .check { width: 100%; height: 100%; }
    .check-circle { fill: none; stroke: #a4d233; stroke-width: 3; stroke-dasharray: 151; stroke-dashoffset: 151; animation: draw 0.6s ease-out forwards; }
    .check-mark { fill: none; stroke: #00a5b5; stroke-width: 4; stroke-linecap: round; stroke-linejoin: round; stroke-dasharray: 40; stroke-dashoffset: 40; animation: draw 0.4s 0.5s ease-out forwards; }

    .confetti {
        position: absolute;
        left: 50%;
        top: 50%;
        width: var(--size);
        height: var(--size);
        opacity: 0;
        animation: confetti-burst 1.3s var(--delay) cubic-bezier(0.15, 0.7, 0.3, 1) forwards;
    }

    @keyframes road-roll { to { background-position: 28px center; } }
    @keyframes wheel-spin { to { transform: rotate(360deg); } }
    @keyframes rider-bob { from { transform: translateY(0); } to { transform: translateY(0.8px); } }
    @keyframes speed-line {
        0% { opacity: 0; transform: translateX(6px); }
        30% { opacity: 0.9; }
        100% { opacity: 0; transform: translateX(-6px); }
    }
    @keyframes draw { to { stroke-dashoffset: 0; } }
    @keyframes confetti-burst {
        0% { opacity: 1; transform: translate(-50%, -50%) rotate(var(--angle)) translateX(0) scale(0.4); }
        70% { opacity: 1; }
        100% { opacity: 0; transform: translate(-50%, -50%) rotate(var(--angle)) translateX(var(--distance)) rotate(540deg) scale(1); }
    }

    @media (prefers-reduced-motion: reduce) {
        .road-fill, .rider .spokes, .rider .upper-body, .rider .speed-lines path, .confetti { animation: none; }
        .rider .speed-lines { display: none; }
    }
</style>
